/**
 * O QUE FALTAVA EM TODO MODAL DESTA BASE.
 *
 * Levantamento antes de escrever, na árvore inteira: `role="dialog"` e
 * `aria-modal` apareciam ZERO vezes, em 16 modais; `Escape` aparecia uma vez, e
 * era o Enter do chat. Na prática isso significa:
 *
 *  · quem usa leitor de tela não é avisado de que uma janela abriu, e continua
 *    lendo a página de trás como se nada tivesse acontecido;
 *  · quem navega por teclado passa o Tab pelo modal e CAI na página atrás dele,
 *    sem ver onde está e sem conseguir voltar;
 *  · Esc não fecha — o único jeito de sair é achar o X com o mouse.
 *
 * Este hook resolve os três de uma vez, e é um hook (não um componente) porque
 * os 16 modais montam a própria casca com os estilos de `_casca.ts`: trocar
 * todos por um componente comum é refactor de outra ordem, e adiar a
 * acessibilidade até lá seria adiar para sempre.
 *
 * AS QUATRO COISAS QUE ELE FAZ
 *
 * 1. ESC FECHA. No `keydown` da janela, em captura, para funcionar mesmo com o
 *    foco dentro de um campo.
 * 2. O FOCO FICA PRESO. Tab no último elemento volta ao primeiro; Shift+Tab no
 *    primeiro vai ao último. Sem isso o Tab escapa para a página de trás.
 * 3. O FOCO ENTRA. Ao abrir, vai para o primeiro elemento focável do modal —
 *    senão o leitor de tela continua no <body>.
 * 4. O FOCO VOLTA. Ao fechar, retorna para o elemento que abriu o modal. É o
 *    que permite abrir, fechar e continuar a navegar de onde se estava.
 *
 * COMO USAR
 *
 *   const { refDoModal, propsDoDialogo } = useModalAcessivel({
 *     aoFechar: handleCloseModal,
 *     idDoTitulo: "titulo-do-item",
 *   });
 *   ...
 *   <ModalContent ref={refDoModal} {...propsDoDialogo}>
 *     <ModalTitle id="titulo-do-item">Editar item</ModalTitle>
 *
 * O `idDoTitulo` não é enfeite: é ele que faz o leitor de tela ANUNCIAR de qual
 * janela se trata ao abrir. Sem ele o anúncio é só "diálogo".
 */
import { useCallback, useEffect, useRef } from "react";

/**
 * O que conta como focável. `:not([disabled])` e `tabindex="-1"` de fora, senão
 * a armadilha de foco leva a pessoa para um botão desligado.
 */
const FOCAVEIS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

interface Opcoes {
  /** Chamado no Esc e usado por quem quiser fechar por fora. */
  aoFechar?: () => void;
  /** `id` do elemento que dá nome à janela — normalmente o título. */
  idDoTitulo?: string;
  /** Desliga a armadilha e o Esc sem desmontar o hook. */
  ativo?: boolean;
}

export function useModalAcessivel({
  aoFechar,
  idDoTitulo,
  ativo = true,
}: Opcoes = {}) {
  const refDoModal = useRef<HTMLDivElement | null>(null);
  const focoAnterior = useRef<HTMLElement | null>(null);

  // O callback muda a cada render do modal; o ouvinte é registrado uma vez.
  // Sem a ref, o Esc chamaria a versão do primeiro render.
  const aoFecharRef = useRef(aoFechar);
  aoFecharRef.current = aoFechar;

  /**
   * A RAIZ DO MODAL, com uma volta pelo DOM quando a `ref` ainda não chegou.
   *
   * Instrumentado no navegador: no momento em que o quadro de animação roda, a
   * `ref` deste hook estava NULA, e por isso o foco nunca entrava — enquanto a
   * MESMA `ref` já estava preenchida quando a armadilha do Tab rodava, mais
   * tarde. Ou seja: o container só chega à ref depois dos primeiros quadros.
   *
   * A busca por `[role="dialog"]` resolve sem depender desse tempo, e é segura
   * aqui porque este produto abre UM modal por vez — todos são montados pelo
   * mesmo `renderModalContent` da Configuração, um de cada vez.
   */
  const raizDoModal = useCallback((): HTMLElement | null => {
    return (
      refDoModal.current ??
      document.querySelector<HTMLElement>('[role="dialog"][aria-modal="true"]')
    );
  }, []);

  const focaveis = useCallback((): HTMLElement[] => {
    const raiz = raizDoModal();
    if (!raiz) return [];
    return Array.from(raiz.querySelectorAll<HTMLElement>(FOCAVEIS)).filter(
      // Elemento escondido não recebe foco; incluí-lo faria o Tab "sumir".
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }, [raizDoModal]);

  /*
   * DOIS EFEITOS, e a separação é o conserto de um defeito medido.
   *
   * Estava tudo num efeito só: guardar quem abriu, entrar com o foco, ouvir as
   * teclas e, na limpeza, devolver o foco. O resultado no navegador foi que a
   * armadilha do Tab funcionava (o evento saía com `defaultPrevented`) e o Esc
   * fechava, mas o foco NUNCA entrava — ficava grudado no botão que abriu.
   *
   * A causa é a limpeza rodar mais de uma vez. Todo ciclo de efeito devolvia o
   * foco para quem abriu, desfazendo a entrada que o quadro seguinte faria. Em
   * desenvolvimento o StrictMode já garante um ciclo extra de propósito, e
   * qualquer remontagem do modal repete o laço.
   *
   * Agora: um efeito SÓ para o par abrir/devolver (dependências vazias, roda no
   * monta e no desmonta), e outro para teclado e entrada de foco. A entrada
   * também virou idempotente — se o foco já está dentro, ela não mexe.
   */
  useEffect(() => {
    focoAnterior.current = document.activeElement as HTMLElement | null;
    return () => {
      // O foco volta para quem abriu. `isConnected` porque aquele botão pode
      // ter saído do DOM enquanto o modal estava aberto.
      const alvo = focoAnterior.current;
      if (alvo && alvo.isConnected) alvo.focus();
    };
  }, []);

  useEffect(() => {
    if (!ativo) return;

    /*
     * O FOCO ENTRA — por TENTATIVAS, e com `setTimeout`, não com
     * `requestAnimationFrame`.
     *
     * A troca saiu de uma medição, não de gosto: com `requestAnimationFrame` a
     * função de foco não rodava NENHUMA vez. O motivo não era o React — era que
     * o navegador NÃO EMITE QUADROS em aba oculta (`document.hidden === true`).
     * Toda a entrada de foco ficava pendurada esperando um quadro que nunca
     * vinha. `setTimeout` continua disparando com a aba escondida.
     *
     * Isso não é só um detalhe de teste: quem abre um modal e troca de aba
     * antes de ele montar cairia no mesmo buraco, e voltaria para uma janela
     * onde o teclado não está em lugar nenhum.
     *
     * A parada também não depende do ciclo do efeito. A tentativa cessa sozinha
     * quando consegue focar, quando o foco já está dentro, ou quando o modal
     * saiu do documento — nenhum desses estados depende de quantas vezes o
     * React decidiu rodar o efeito (o StrictMode roda duas de propósito).
     *
     * As tentativas existem porque um tique não basta: o conteúdo do modal
     * aparece depois do container, então a primeira varredura pode achar lista
     * vazia. Se depois de ~20 tentativas ainda não houver nada focável, o foco
     * vai para o próprio container (que tem `tabindex="-1"`), para o leitor de
     * tela pelo menos entrar na janela.
     */
    let tentativas = 0;
    const tentarFocar = () => {
      const raiz = raizDoModal();
      // O modal saiu do DOM: não há nada a focar, e insistir seria vazamento.
      if (!raiz || !raiz.isConnected) {
        // Ainda pode estar montando: tenta de novo até o teto.
        if (tentativas++ < 20) setTimeout(tentarFocar, 16);
        return;
      }
      // Já está dentro: não roubar o foco de onde a pessoa está.
      if (raiz.contains(document.activeElement)) return;

      const lista = focaveis();
      if (lista.length > 0) {
        lista[0].focus();
        return;
      }
      if (tentativas++ < 20) {
        setTimeout(tentarFocar, 16);
        return;
      }
      raiz.focus();
    };
    setTimeout(tentarFocar, 0);

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") {
        evento.stopPropagation();
        aoFecharRef.current?.();
        return;
      }
      if (evento.key !== "Tab") return;

      const lista = focaveis();
      if (lista.length === 0) return;

      const primeiro = lista[0];
      const ultimo = lista[lista.length - 1];
      const atual = document.activeElement;

      // A volta é feita à mão porque o Tab do navegador não conhece a fronteira
      // do modal: ele seguiria para a página de trás.
      if (evento.shiftKey && (atual === primeiro || !raizDoModal()?.contains(atual))) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && atual === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      }
    };

    // Captura: o modal precisa ver a tecla antes de um campo que dê
    // `stopPropagation` no próprio keydown.
    window.addEventListener("keydown", aoTeclar, true);

    return () => {
      window.removeEventListener("keydown", aoTeclar, true);
    };
  }, [ativo, focaveis, raizDoModal]);

  return {
    refDoModal,
    propsDoDialogo: {
      role: "dialog" as const,
      "aria-modal": true,
      ...(idDoTitulo ? { "aria-labelledby": idDoTitulo } : {}),
      // -1 para o próprio container poder receber foco quando não há nada
      // focável dentro (modal só de texto).
      tabIndex: -1,
    },
  };
}
